// Orientation Layer lesson data

export interface OrientationSection {
  heading?: string;
  paragraphs: string[];
  dividerBefore?: boolean; // render an amber divider before this section
}

export interface OrientationCopyOption {
  label: string; // e.g. "Option A — The Email You've Been Putting Off"
  intro: string[]; // paragraphs before the copy box
  prompt: string; // the prompt text in the dark navy copy box
  outro?: string[]; // paragraphs after the copy box
}

export interface OrientationBlock {
  type: 'situation' | 'anna' | 'why' | 'try' | 'prompt' | 'main' | 'tools' | 'copyoptions' | 'reflection' | 'beforewestart';
  content?: string; // legacy single-string (still used by why/try/prompt)
  sections?: OrientationSection[]; // multi-section rich content (situation, main, anna)
  label?: string; // override the default card label
  copyOptions?: OrientationCopyOption[]; // used by 'copyoptions' block in O4
  reflectionResponses?: string[]; // used by 'reflection' block in O4
}

export interface OrientationLesson {
  id: string;
  slug: string; // O1, O2, O3, O4
  number: number;
  title: string;
  blocks: OrientationBlock[];
  nextLabel?: string; // override next button label
}

export const ORIENTATION_LESSONS: OrientationLesson[] = [
  {
    id: 'o1',
    slug: 'O1',
    number: 1,
    title: 'You Are Not Behind',
    blocks: [
      // ── Card 1: The Situation ──────────────────────────────────────────────
      {
        type: 'situation',
        sections: [
          {
            paragraphs: [
              'Six months ago you were in a meeting where everyone talked about AI like it was the most obvious thing in the world.',
              'ChatGPT this. Copilot that. Someone mentioned a tool you\'d never heard of and three people nodded like it was common knowledge.',
              'You nodded too.',
              'That evening you googled it. What came back was either written for IT departments, aimed at people who already knew what they were doing, or so full of jargon that you closed the tab after ninety seconds and told yourself you\'d come back to it.',
              'You didn\'t come back to it.',
            ],
          },
          {
            paragraphs: [
              'Or maybe it wasn\'t a meeting. Maybe it was a news headline that made you uneasy. Or a comment from a colleague who seemed to find all of this effortless. Or a slow, quiet sense that something important was happening and nobody had explained it to you in a way that actually made sense.',
            ],
          },
          {
            paragraphs: [
              'Whatever the version — you\'re here now. And the first thing I want to tell you is this:',
            ],
          },
          {
            heading: 'You are not behind.',
            paragraphs: [],
          },
        ],
      },

      // ── Card 2: Main Content ───────────────────────────────────────────────
      {
        type: 'main',
        sections: [
          {
            heading: 'Not behind. Underserved.',
            paragraphs: [
              'Here is what actually happened.',
              'AI tools became genuinely useful for ordinary professional tasks in about 2023. Before that, they existed — but they weren\'t the kind of thing most working adults needed to pay attention to.',
              'Then they were.',
              'And the people who wrote about them — the guides, the courses, the LinkedIn posts, the YouTube videos — mostly wrote for one of two audiences.',
              'The first: technical professionals who wanted to understand how AI works at a deeper level.',
              'The second: younger generalists who seemed to find all of this intuitive and just needed to know which buttons to press.',
              'If you had twenty or thirty years of professional experience and wanted someone to explain this clearly, at your level, in your context — there wasn\'t much.',
              'That\'s not your fault. That\'s an explanation problem.',
              'The tools are not complicated. Nobody explained them properly to you.',
              'That\'s what this is for.',
            ],
          },
          {
            heading: 'What you actually have.',
            paragraphs: [
              'Before we go any further, I want to say something about the experience you\'re bringing to this.',
              'Twenty or thirty years of professional life gives you things that no amount of AI enthusiasm can replicate.',
              'You know how organisations work. You know how to read a room. You know the difference between what someone says and what they mean. You know how to write clearly, present confidently, manage difficult conversations, and make decisions under pressure.',
              'None of that disappears because AI exists.',
              'What AI does is handle some of the time-consuming, repetitive, or technically awkward parts of professional work — so that the judgment, the relationships, and the experience you\'ve built over decades become more valuable, not less.',
              'The people who will use AI best are not necessarily the ones who understand it most deeply.',
              'They\'re the ones who know what they\'re trying to achieve and can direct a capable tool toward it.',
              'That\'s you. Once you know how to use the tool.',
            ],
          },
          {
            heading: 'One more thing.',
            paragraphs: [
              'You may have tried some of these tools already and found them underwhelming, confusing, or not worth the effort.',
              'That\'s almost certainly a prompting problem, not an AI problem. Most people\'s first attempts at using AI produce mediocre results because they approach it like a search engine — type a few words, see what comes back.',
              'That\'s not how it works. We\'ll cover exactly how it does work in Lesson 4.',
              'For now: if you tried it and it disappointed you, that doesn\'t mean it isn\'t useful. It means you haven\'t been shown how to use it yet.',
              'That\'s what the next four lessons are for.',
            ],
          },
        ],
      },

      // ── Card 3: Anna's Note ────────────────────────────────────────────────
      {
        type: 'anna',
        sections: [
          {
            paragraphs: [
              'I want to tell you about the moment this shifted for me.',
              'I was preparing for a board presentation. I had the data, I had the argument, I had the slides. What I didn\'t have was confidence that I\'d anticipated every difficult question the room was likely to ask.',
              'On a whim, I typed what I was presenting into one of these tools and asked it to play devil\'s advocate. To give me the five strongest arguments against my own recommendation.',
              'What came back was uncomfortable. It found two things I\'d glossed over. Real weaknesses in my argument that I\'d half-noticed and quietly ignored.',
              'I fixed them before the presentation. The board asked a version of one of those questions. I had the answer.',
              'That was it. That was the moment I stopped seeing AI as something I needed to understand and started seeing it as something genuinely useful.',
              'I didn\'t need to know how it worked. I needed to know what to ask it.',
              'That\'s the whole thing, really.',
            ],
          },
        ],
      },

      // ── Card 4 (Anna Filter) — skipped: not live yet ───────────────────────

      // ── Card 5: Close / Next Step ──────────────────────────────────────────
      {
        type: 'try',
        sections: [
          {
            heading: 'Right. Let\'s start.',
            paragraphs: [
              'You\'ve just done the hardest part — you showed up.',
              'The next lesson takes about five minutes. It answers the question that most people are too embarrassed to ask out loud: what is AI, actually?',
              'Not the technical version. Not the breathless version. Just a plain answer to a reasonable question.',
            ],
          },
        ],
      },
    ],
    nextLabel: 'Next: What AI Actually Is →',
  },
  {
    id: 'o2',
    slug: 'O2',
    number: 2,
    title: 'What AI Actually Is',
    blocks: [
      // ── Card 1: The Situation ──────────────────────────────────────────────
      {
        type: 'situation',
        sections: [
          {
            paragraphs: [
              'The headlines have not been subtle.',
              'AI is going to change everything. AI is going to take your job. AI is smarter than humans. AI is dangerous. AI is the most important invention since the internet. AI is overhyped. AI is already obsolete. AI is the future. The future is already here.',
              'It is very hard to know what to make of any of this when nobody has stopped to explain what AI actually is in plain English.',
              'Most explanations either go too deep — neural networks, large language models, training data, parameters — or stay too vague. "It\'s like a very smart computer." Well. Yes. But that doesn\'t help you decide whether to use it for your quarterly report.',
              'Here is the explanation I wish I\'d had at the start.',
              'It takes about five minutes and it will make everything that follows considerably less confusing.',
            ],
          },
        ],
      },

      // ── Card 2: Main Content ───────────────────────────────────────────────
      {
        type: 'main',
        sections: [
          {
            heading: 'The plain English version.',
            paragraphs: [
              'AI — the kind you\'re going to use at work and in life — is a system that has been trained on an enormous amount of text.',
              'Books, articles, websites, documents, conversations. Billions of them. More than any human could read in a thousand lifetimes.',
              'From all of that, it has learned patterns. How language works. How ideas connect. What tends to follow what. What a good answer to a given question usually looks like.',
              'When you type something into an AI tool, it uses those patterns to generate a response that is likely to be useful given what you asked.',
              'That\'s it. That\'s the mechanism.',
              'It is not thinking in the way you think. It is not conscious. It does not have opinions, feelings, or an agenda.',
              'It is an extraordinarily capable pattern-matching system that has read more than anyone and can produce fluent, useful, well-structured responses very quickly.',
              'Which, for most professional purposes, is exactly what you need.',
            ],
          },
          {
            heading: 'Why it feels so different from everything before it.',
            paragraphs: [
              'Every tool you\'ve used in your career has done what you told it to do. Exactly that. No more.',
              'A spreadsheet calculates what you enter. A search engine finds pages matching your words. A word processor formats what you type.',
              'AI is different because you can talk to it in ordinary language and it understands what you mean — not just what you literally said.',
              'You can say: "Help me write a polite but firm email to a client who has missed three deadlines without damaging the relationship."',
              'And it will write that email.',
              'Not a template. Not a list of suggestions. An actual draft, in professional language, that you can read, adjust, and send.',
              'That shift — from tool that does exactly what you tell it to assistant that understands what you\'re trying to achieve — is why AI feels different from everything that came before it.',
              'Because it is.',
            ],
          },
          {
            heading: 'The honest limitations.',
            paragraphs: [
              'I want to be straight with you about two things before we go further, because nobody told me these at the start and I had to find them out the hard way.',
            ],
          },
          {
            heading: 'One thing to know: it gets things wrong.',
            paragraphs: [
              'AI produces fluent, confident-sounding responses. It does this even when the response is incorrect. It does not flag uncertainty the way a cautious colleague might. It just answers.',
              'This means you need to check what it produces — especially anything involving facts, figures, dates, names, or anything where accuracy matters. Use it to draft, to think, to structure. Apply your own judgment to the output.',
              'You are still in charge. The tool is helpful, not infallible.',
            ],
          },
          {
            dividerBefore: true,
            heading: 'Another thing to know: it doesn\'t know you.',
            paragraphs: [
              'AI has no memory of previous conversations unless you\'re using a tool that specifically provides one. Each session starts fresh. It doesn\'t know your industry, your organisation, your colleagues, or your preferences unless you tell it.',
              'This is why giving it context matters so much. The more you tell it about your situation, the more useful it becomes. We\'ll cover this properly in Lesson 4.',
              'These aren\'t reasons not to use it. They\'re reasons to use it intelligently. Which is what you\'re here to learn.',
            ],
          },
        ],
      },

      // ── Card 3: Anna's Note ────────────────────────────────────────────────
      {
        type: 'anna',
        sections: [
          {
            paragraphs: [
              'The moment AI stopped feeling overwhelming to me was the moment I stopped trying to understand how it worked and started thinking about what I wanted it to do.',
              'I don\'t understand how my car engine works either. I understand that when I want to go somewhere, I drive. I know it needs fuel. I know it has limits. I know not to drive it into a lake.',
              'That\'s about the level of technical understanding you need for AI tools too.',
              'What they are. What they\'re good at. What to watch out for. How to direct them toward what you need.',
              'Everything else — the architecture, the training runs, the compute requirements — is genuinely interesting if that\'s your thing. It doesn\'t make you better at using the tools.',
              'Using the tools makes you better at using the tools.',
              'That\'s what the rest of this course is.',
            ],
          },
        ],
      },

      // ── Card 4: Close / Next Step ──────────────────────────────────────────
      {
        type: 'try',
        sections: [
          {
            heading: 'That\'s the foundation.',
            paragraphs: [
              'You now know more about what AI actually is than most people who use it every day. They just started using it without anyone explaining the basics. You haven\'t.',
              'Next up: a plain-English introduction to the main AI tools — what they are, what each one does, and which one to start with.',
              'It\'s shorter than you\'d expect. The landscape is less complicated than the coverage suggests.',
            ],
          },
        ],
      },
    ],
    nextLabel: 'Next: Meet The AI Tools →',
  },
  {
    id: 'o3',
    slug: 'O3',
    number: 3,
    title: 'Meet The AI Tools',
    blocks: [
      // ── Card 1: The Situation ──────────────────────────────────────────────
      {
        type: 'situation',
        sections: [
          {
            paragraphs: [
              'At some point in the last year or two you have probably encountered at least three of the following:',
              'ChatGPT. Copilot. Claude. Gemini. Perplexity. Grok. Midjourney. DALL-E. Sora. Llama.',
              'Some of them sound like medical conditions. Several of them appear to do the same thing. None of them came with a plain-English explanation of what they actually are and whether you need them.',
              'The honest answer is that you do not need most of them. The landscape is less complicated than it looks once you understand what each category does.',
              'This lesson covers the five tools that are actually relevant to professionals in everyday work and life. Five. Not ten, not twenty.',
              'By the end of it you\'ll know what each one is for, which one to start with, and why the rest can wait.',
            ],
          },
        ],
      },

      // ── Card 2: The Five Tools ─────────────────────────────────────────────
      {
        type: 'tools',
        sections: [],
      },

      // ── Card 3: Which One Should You Use ──────────────────────────────────
      {
        type: 'main',
        sections: [
          {
            heading: 'Which one should you use?',
            paragraphs: [
              'The one you have access to.',
              'If your company has rolled out Copilot — use that. It\'s already in your tools. It\'s where the most immediate time savings are.',
              'If you don\'t have Copilot yet — use ChatGPT. It\'s free, it\'s capable, and it requires nothing from your IT department.',
              'Once you\'re comfortable and want to add a research tool, Perplexity is the one I\'d reach for first.',
              'Once you\'re comfortable with one tool, the others are easy to pick up. The principles of using them well are the same across all of them. That\'s what the next lesson is about.',
              'You do not need all five. You do not need to become an expert in any of them. You need to become comfortable with one — and then let that confidence spread naturally to the others.',
              'That\'s what happens when you understand the underlying skill rather than just one tool.',
            ],
          },
        ],
      },

      // ── Card 4: Anna's Note ────────────────────────────────────────────────
      {
        type: 'anna',
        sections: [
          {
            paragraphs: [
              'When I started, I made the mistake of trying to understand all of the tools at once.',
              'I signed up for accounts. I compared features. I read articles about which one was best. I spent more time researching AI tools than actually using any of them.',
              'What broke the loop was a colleague who told me: pick one, use it for a week, then decide if you need another one.',
              'I picked ChatGPT because it was the one I\'d heard of. I used it for a week. By the end of the week I understood what prompting actually was, what good output looked like, and what kinds of tasks AI was genuinely useful for.',
              'Then I tried Claude. Then Perplexity. Each one took about an afternoon to get comfortable with because I already knew what I was doing — I\'d just learned it on a different tool.',
              'That\'s the sequence. One first. The rest follow.',
            ],
          },
        ],
      },

      // ── Card 5: Close / Next Step ──────────────────────────────────────────
      {
        type: 'try',
        sections: [
          {
            heading: 'One more lesson before the main course.',
            paragraphs: [
              'You know what AI is. You know which tools exist and which one to start with.',
              'The last thing to cover before Module 1 is the most practical: how to actually talk to AI in a way that gets useful results.',
              'This is the lesson most people skip — and it\'s why most people\'s first attempts at using AI are underwhelming.',
              'It takes about ten minutes. And you\'ll try something yourself at the end of it.',
            ],
          },
        ],
      },
    ],
    nextLabel: 'Next: How To Talk To AI →',
  },
  {
    id: 'o4',
    slug: 'O4',
    number: 4,
    title: 'How To Talk To AI',
    blocks: [
      // ── Card 1: The Situation ──────────────────────────────────────────────
      {
        type: 'situation',
        sections: [
          {
            paragraphs: [
              'You have opened the AI tool. There is a text box. There is a cursor blinking in it.',
              'And you have absolutely no idea what to type.',
              'This is where most people\'s first experience with AI goes wrong. They type something vague — "help me with emails" — and get something vague back. Then they close the tab and conclude that AI isn\'t as useful as everyone says.',
              'It isn\'t the tool. It\'s the question.',
              'AI is not a search engine. You are not typing keywords and hoping for relevant results. You are having a conversation with something that can understand nuance, context, and intent — but only if you give it enough to work with.',
              'The difference between a mediocre AI response and a genuinely useful one is almost always in the quality of the question.',
              'This lesson teaches you how to ask better questions. It takes about ten minutes and by the end of it you will have tried it yourself on something real.',
            ],
          },
        ],
      },

      // ── Card 2: Four Principles ────────────────────────────────────────────
      {
        type: 'main',
        sections: [
          {
            heading: 'Four principles. That\'s all.',
            paragraphs: [
              'These are not rules. They are habits. The more naturally they come, the better your results will be.',
            ],
          },
          {
            heading: 'Principle 1 — Be specific.',
            paragraphs: [
              'Vague question. Vague answer. Every time.',
              'The more specific you are about what you want, who it\'s for, and what good looks like — the more useful the response.',
              'Not useful: "Help me with this email."',
              'More useful: "Help me write a polite but firm reply to a client who has missed two deadlines. I want to express concern without damaging the relationship. Keep it under 150 words."',
              'The second version tells AI: what you need, the emotional tone, the constraint, and the context. It gets a far better result.',
            ],
          },
          {
            heading: 'Principle 2 — Give context.',
            paragraphs: [
              'AI knows nothing about you unless you tell it.',
              'It doesn\'t know your industry, your organisation, your role, or your audience. Every session starts fresh. Context you give it in one message doesn\'t carry forward to the next unless you\'re using a tool with memory features.',
              'So tell it who you are and what you\'re trying to do.',
              '"I\'m a project manager at a mid-size manufacturing company. I need to send an update to my board..."',
              'That sentence changes everything that follows. It takes ten seconds to add and dramatically improves what comes back.',
            ],
          },
          {
            heading: 'Principle 3 — Iterate.',
            paragraphs: [
              'Your first prompt does not need to be perfect.',
              'This is not a one-shot search. It is a conversation.',
              'If what comes back isn\'t quite right — ask it to try again. Tell it what to change. Ask it to be shorter, warmer, more direct, less formal, more specific. Ask it to explain its reasoning. Ask it to give you three alternatives.',
              '"That\'s close but a bit too formal — can you make it sound more like a normal email, less like a corporate announcement?"',
              'That\'s a perfectly good second prompt. It works. Most people never send a second prompt because they assume the first attempt is the only one. It isn\'t.',
            ],
          },
          {
            heading: 'Principle 4 — Check what comes back.',
            paragraphs: [
              'AI is confident even when wrong.',
              'Read what it produces. Apply your own judgment. You know your situation, your organisation, your audience. The AI doesn\'t. You are the quality check — not because AI is unreliable, but because you are the one who knows what accurate looks like in this specific context.',
              'This is not a weakness in the tool. It is how the tool is designed to be used. You direct it. It drafts. You judge.',
            ],
          },
        ],
      },

      // ── Card 3: Try It Now ─────────────────────────────────────────────────
      {
        type: 'copyoptions',
        label: 'Try It Now',
        copyOptions: [
          {
            label: 'Option A — The Email You\'ve Been Putting Off',
            intro: [
              'Open your AI tool. Type this — filling in the detail that applies to your situation:',
            ],
            prompt: 'I need to write an email to [describe the recipient and your relationship]. The situation is: [describe what\'s happened or what you need to say]. The tone should be [professional and warm / firm but fair / direct without being harsh — choose what fits]. Keep it under 200 words.',
            outro: [
              'Read what comes back. It won\'t be perfect — it never is on the first attempt. But it will give you something to work with instead of a blank page.',
            ],
          },
          {
            label: 'Option B — Something You Need To Understand',
            intro: [
              'A piece of regulation. A market trend. A technology your organisation is adopting. Anything where you want a plain English explanation.',
            ],
            prompt: 'Explain [topic] in plain English as if you\'re talking to an intelligent professional who knows nothing about it. What is it, why does it matter, and what do I actually need to understand about it to use that knowledge at work?',
            outro: [
              'Check what comes back against what you already know. See where it\'s accurate. Note where you\'d want to verify further.',
            ],
          },
          {
            label: 'Option C — Something That\'s Been On Your List',
            intro: [
              'A task that keeps appearing on your to-do list and never quite getting done. Not because it\'s urgent — because it\'s slightly tedious or you don\'t know where to start.',
              'Could be: planning something, drafting something, organising something, thinking through something.',
            ],
            prompt: 'I need to [describe the task]. Here\'s the relevant context: [describe what you know, what the constraints are, and what a good outcome looks like]. Help me make a start.',
          },
        ],
      },

      // ── Reflection card (below Try It Now) ────────────────────────────────
      {
        type: 'reflection',
        reflectionResponses: [
          '"That was more useful than I expected."',
          '"I can see how this works — I\'d do it differently next time."',
          '"I need to give it more context."',
        ],
      },

      // ── Card 4: Anna's Note ────────────────────────────────────────────────
      {
        type: 'anna',
        sections: [
          {
            paragraphs: [
              'The first time I got a genuinely useful response from AI, I laughed out loud.',
              'Not because it was clever. Because it was obvious — in retrospect — that the reason my previous attempts had been disappointing was entirely down to how I\'d asked.',
              'I\'d been treating it like Google. Typing fragments. Getting fragments back. Concluding it wasn\'t that impressive.',
              'The prompt that worked was longer. It had context. It had a specific ask. It had a tone requirement. It took me about forty seconds to write.',
              'What came back took me thirty seconds to read and saved me twenty minutes of work.',
              'That ratio — forty seconds in, twenty minutes saved — is roughly what you can expect when you use it well. Not magic. Just genuinely useful.',
              'The prompts in the main course are all written to work first time. You\'ll see the pattern quickly. After a few modules it becomes instinct.',
              'Right. That\'s the orientation done.',
              'Let\'s get to work.',
            ],
          },
        ],
      },

      // ── Card 5: One practical thing before Module 1 (Before We Start) ────
      {
        type: 'beforewestart' as const,
      },

      // ── Card 6: Close / Into the Main Course ──────────────────────────────
      {
        type: 'try',
        sections: [
          {
            heading: 'You\'re ready.',
            paragraphs: [
              'Four lessons. Twenty-five minutes or so.',
              'You know what AI is, which tools matter, and how to talk to one. You\'ve tried it yourself on something real.',
              'That puts you ahead of most people who have been using these tools for months without anyone explaining the basics.',
              'Module 1 starts with Copilot — the AI built into the tools most professionals already use every day. If you don\'t have Copilot yet, everything transfers directly to ChatGPT.',
              'The first lesson takes about five minutes. By the end of it you\'ll have used AI to do something that will save you time tomorrow.',
            ],
          },
        ],
      },
    ],
    nextLabel: 'Start Module 1: Copilot at Work →',
  },
];
